import React, { useEffect, useRef, useState } from "react";
import Editor from "@monaco-editor/react";
import { useTheme, monacoTheme } from "../ThemeContext";

// --- tiny flat icons (currentColor, single stroke) ---
const ico = (paths) => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {paths}
  </svg>
);
const CopyIcon = () =>
  ico(
    <>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </>
  );
const PasteIcon = () =>
  ico(
    <>
      <rect x="8" y="4" width="8" height="4" rx="1" />
      <path d="M9 6H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3" />
    </>
  );
const ClearIcon = () =>
  ico(
    <>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </>
  );
const DownloadIcon = () =>
  ico(
    <>
      <path d="M12 3v12" />
      <path d="m7 11 5 5 5-5" />
      <path d="M5 21h14" />
    </>
  );
const PlayIcon = () => ico(<polygon points="7 4 19 12 7 20 7 4" />);
const BoltIcon = () => ico(<polygon points="13 2 4 14 11 14 10 22 20 9 13 9 13 2" />);
const ResetIcon = () =>
  ico(
    <>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
    </>
  );

function Kbd({ combo, label }) {
  const keys = combo.split("+");
  return (
    <span className="sc">
      <span className="keycap-group">
        {keys.map((k, i) => (
          <kbd className="keycap" key={i}>
            {k}
          </kbd>
        ))}
      </span>
      {label}
    </span>
  );
}

const bytes = (s) => new TextEncoder().encode(s || "").length;
const lines = (s) => (s ? s.split("\n").length : 0);
const fmtBytes = (n) => (n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`);

// Collapse a toolbar's text labels (icon-only) when they'd overflow the header.
// Hysteresis: we remember the width at which it overflowed, and only re-expand
// once there's that width + a buffer of slack — so toggling the labels (which
// changes the content width) can never trigger an immediate reverse, i.e. no
// show→wrap→hide→fit→show loop. The resize handler is debounced on top of that.
function useCompactToolbar() {
  const ref = useRef(null);
  const [compact, setCompact] = useState(false);
  const compactRef = useRef(false);
  const neededRef = useRef(0); // header content width measured while overflowing

  useEffect(() => {
    compactRef.current = compact;
  }, [compact]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const BUFFER = 16;
    let timer = null;

    const evaluate = () => {
      const node = ref.current;
      if (!node) return;
      if (!compactRef.current) {
        if (node.scrollWidth > node.clientWidth + 1) {
          neededRef.current = node.scrollWidth; // intrinsic expanded width
          setCompact(true);
        }
      } else if (neededRef.current && node.clientWidth >= neededRef.current + BUFFER) {
        setCompact(false);
      }
    };

    const onResize = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(evaluate, 120);
    };

    evaluate(); // initial pass, no debounce
    const ro = new ResizeObserver(onResize);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);

  return [ref, compact];
}

export default function FormatterLayout({
  title = "Formatter",
  kind = "Formatter",
  input,
  setInput,
  output,
  setOutput,
  onFormat,
  onMinify,
  onStructify,
  onReset,
  onRevert,
  canRevert = false,
  singlePane = false,
  sourceLanguage = "plaintext",
  targetLanguage = "plaintext",
  hasStruct = false,
}) {
  const { theme } = useTheme();
  const editorTheme = monacoTheme(theme);

  // Clear input, output, and the struct view all at once.
  const clearAll = () => {
    setInput("");
    setOutput("");
    if (onStructify) onStructify("");
  };

  // ⌘/Ctrl+Enter → Convert · ⌘/Ctrl+Del → clear · ⌘/Ctrl+Shift+Enter → minify.
  // Capture phase so we win over Monaco's editor keybindings when it's focused.
  useEffect(() => {
    function onKeyDown(e) {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      if (e.shiftKey) {
        // ⌘/Ctrl+Shift+Enter → minify
        if (e.key === "Enter" && onMinify) {
          e.preventDefault();
          e.stopPropagation();
          onMinify();
        }
        return;
      }
      if (e.key === "Enter" && onFormat) {
        e.preventDefault();
        e.stopPropagation();
        onFormat();
      } else if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        e.stopPropagation();
        clearAll();
      }
    }
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [onFormat, onMinify]);

  const [inHeaderRef, inCompact] = useCompactToolbar();
  const [outHeaderRef, outCompact] = useCompactToolbar();

  // This is a formatter/converter — the editors show already-valid output, so
  // Monaco's language-server validation only produces noise (empty-rule warnings
  // on our CSS, "undefined global" errors on JS snippets, etc.). Disable it.
  const handleBeforeMount = (monaco) => {
    const off = { noSemanticValidation: true, noSyntaxValidation: true, noSuggestionDiagnostics: true };
    try {
      monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions(off);
      monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions(off);
    } catch {}
    try {
      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({ validate: false });
    } catch {}
    try {
      monaco.languages.css.cssDefaults.setOptions({ validate: false });
      monaco.languages.css.scssDefaults.setOptions({ validate: false });
      monaco.languages.css.lessDefaults.setOptions({ validate: false });
    } catch {}
  };

  const editorOptions = {
    minimap: { enabled: false },
    fontSize: 14,
    wordWrap: "on",
    scrollBeyondLastLine: false,
    padding: { top: 10 },
    smoothScrolling: true,
  };

  const copy = (text) => navigator.clipboard?.writeText(text || "");
  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setInput(text);
      if (onStructify) onStructify(text);
    } catch {}
  };
  const clear = () => {
    setInput("");
    if (onStructify) onStructify("");
  };
  const download = (text) => {
    const blob = new Blob([text || ""], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `formatted.${targetLanguage === "javascript" ? "js" : targetLanguage}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const head = (primaryLabel) => (
    <div className="workspace-head">
      <div>
        <h1 className="title">
          {title}
          <span className="title-sep"> / </span>
          <span className="title-kind">{kind}</span>
        </h1>
        <div className="workspace-meta">
          SECURITY · unlike others, we do not save or sell this data
        </div>
      </div>

      <div className="head-shortcuts">
        <Kbd combo="⌘+↵" label={primaryLabel} />
        <Kbd combo="⌘+⌫" label="clear" />
        {onMinify && <Kbd combo="⌘+⇧+↵" label="minify" />}
        <Kbd combo="⌘+B" label="options" />
        <Kbd combo="⌘+L" label="struct" />
        <Kbd combo="⌘+I" label="theme" />
      </div>
    </div>
  );

  // Single-pane mode: one editor, formatted in place by the primary button.
  // Used by the Formatter (same language in/out); the Converter stays two-pane.
  if (singlePane) {
    return (
      <div className="formatter-page">
        {head("format")}
        <div className="formatter-flex single">
          <div className="editor-box">
            <div
              className={`editor-header ${inCompact ? "compact" : ""}`}
              ref={inHeaderRef}
            >
              <span className="editor-head-left">
                <span className="editor-title">Editor</span>
                <span className="lang-badge">{sourceLanguage}</span>
                <span className="byte-count">{fmtBytes(bytes(input))}</span>
              </span>
              <div className="buttons">
                <button className="tool-btn" onClick={() => copy(input)}>
                  <CopyIcon /><span className="btn-label">Copy</span>
                </button>
                <button className="tool-btn" onClick={paste}>
                  <PasteIcon /><span className="btn-label">Paste</span>
                </button>
                <button className="tool-btn" onClick={clear}>
                  <ClearIcon /><span className="btn-label">Clear</span>
                </button>
                {onRevert && canRevert && (
                  <button
                    className="tool-btn"
                    onClick={onRevert}
                    title="Restore the buffer from before the last Format"
                  >
                    <ResetIcon /><span className="btn-label">Revert</span>
                  </button>
                )}
                {onMinify && (
                  <button className="tool-btn" onClick={onMinify}>
                    <BoltIcon /><span className="btn-label">Minify</span>
                  </button>
                )}
                <button className="tool-btn" onClick={() => download(input)}>
                  <DownloadIcon /><span className="btn-label">Download</span>
                </button>
                {onReset && (
                  <button
                    className="tool-btn"
                    onClick={onReset}
                    title="Restore the sample input and default options"
                  >
                    <ResetIcon /><span className="btn-label">Reset</span>
                  </button>
                )}
                <button
                  className="primary"
                  onClick={onFormat}
                  title="Format (⌘/Ctrl+Enter)"
                >
                  <PlayIcon /><span className="btn-label">Format</span>
                </button>
              </div>
            </div>
            <div className="editor-body">
              <Editor
                height="100%"
                language={sourceLanguage}
                theme={editorTheme}
                beforeMount={handleBeforeMount}
                value={input}
                onChange={(v) => {
                  setInput(v || "");
                  if (onStructify) onStructify(v || "");
                }}
                options={editorOptions}
              />
            </div>
            <div className="editor-status">
              <span>UTF-8</span>
              <span>LF</span>
              <span>{lines(input)} lines</span>
              <span className="ready">ready</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="formatter-page">
      {head("convert")}

      <div className="formatter-flex">
        {/* Input editor */}
        <div className="editor-box">
          <div
            className={`editor-header ${inCompact ? "compact" : ""}`}
            ref={inHeaderRef}
          >
            <span className="editor-head-left">
              <span className="editor-title">Input</span>
              <span className="lang-badge">{sourceLanguage}</span>
              <span className="byte-count">{fmtBytes(bytes(input))}</span>
            </span>
            <div className="buttons">
              <button className="tool-btn" onClick={() => copy(input)}>
                <CopyIcon /><span className="btn-label">Copy</span>
              </button>
              <button className="tool-btn" onClick={paste}>
                <PasteIcon /><span className="btn-label">Paste</span>
              </button>
              <button className="tool-btn" onClick={clear}>
                <ClearIcon /><span className="btn-label">Clear</span>
              </button>
              {onReset && (
                <button
                  className="tool-btn"
                  onClick={onReset}
                  title="Restore the sample input and default options"
                >
                  <ResetIcon /><span className="btn-label">Reset</span>
                </button>
              )}
              <button
                className="primary"
                onClick={onFormat}
                title="Convert (⌘/Ctrl+Enter)"
              >
                <PlayIcon /><span className="btn-label">Convert</span>
              </button>
            </div>
          </div>
          <div className="editor-body">
            <Editor
              height="100%"
              language={sourceLanguage}
              theme={editorTheme}
              beforeMount={handleBeforeMount}
              value={input}
              onChange={(v) => {
                setInput(v || "");
                if (onStructify) onStructify(v || "");
              }}
              options={editorOptions}
            />
          </div>
          <div className="editor-status">
            <span>UTF-8</span>
            <span>LF</span>
            <span>{lines(input)} lines</span>
            <span className="ready">ready</span>
          </div>
        </div>

        {/* Output editor */}
        <div className="editor-box">
          <div
            className={`editor-header ${outCompact ? "compact" : ""}`}
            ref={outHeaderRef}
          >
            <span className="editor-head-left">
              <span className="editor-title">Output</span>
              <span className="lang-badge">{targetLanguage}</span>
              <span className="byte-count">{fmtBytes(bytes(output))}</span>
            </span>
            <div className="buttons">
              <button className="tool-btn" onClick={() => copy(output)}>
                <CopyIcon /><span className="btn-label">Copy</span>
              </button>
              <button className="tool-btn" onClick={() => download(output)}>
                <DownloadIcon /><span className="btn-label">Download</span>
              </button>
              {onMinify && (
                <button className="tool-btn" onClick={onMinify}>
                  <BoltIcon /><span className="btn-label">Minify</span>
                </button>
              )}
            </div>
          </div>
          <div className="editor-body">
            <Editor
              height="100%"
              language={targetLanguage}
              theme={editorTheme}
              beforeMount={handleBeforeMount}
              value={output}
              onChange={(v) => setOutput(v || "")}
              options={editorOptions}
            />
          </div>
          <div className="editor-status">
            <span>UTF-8</span>
            <span>LF</span>
            <span>{lines(output)} lines</span>
            <span className="ready">ready</span>
          </div>
        </div>
      </div>
    </div>
  );
}
